import React, { useEffect, useState, useCallback } from 'react';
import { getApiCreatedSlackChannels } from '../../services/SlackChannelsService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from '../../Footer';

/**
 * Read-only view of Slack channels created through the SlackChannelsController API (as opposed to
 * hand-entered slack channel configurations). Not linked from the nav — reachable only by typing the URL.
 */
function SlackChannels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getApiCreatedSlackChannels();
      setChannels(data || []);
    } catch (err) {
      setError('Failed to load Slack channels');
      toast.error('Failed to load Slack channels');
      setChannels([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  return (
    <div style={{ paddingLeft: 25, paddingRight: 25, width: '100%', minHeight: '100vh' }} className="mt-4">
      <ToastContainer />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">API-Created Slack Channels</h4>
        <button
          className="btn btn-outline-secondary d-flex align-items-center"
          onClick={fetchChannels}
          disabled={loading}
          title="Refresh"
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          ) : (
            <i className="bi bi-arrow-clockwise me-2"></i>
          )}
          Refresh
        </button>
      </div>

      {loading && channels.length === 0 ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Channel Name</th>
                <th>Channel ID</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {channels.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-4">
                    No API-created channels found
                  </td>
                </tr>
              ) : (
                channels.map((channel) => (
                  <tr key={channel.id}>
                    <td>
                      <span className="fw-semibold">{channel.name}</span>
                    </td>
                    <td>
                      <code className="small">{channel.slackChannelId}</code>
                    </td>
                    <td>
                      <span className={`badge ${channel.isActive ? 'bg-success' : 'bg-secondary'}`}>
                        {channel.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="small text-muted">
                      {channel.createdAt ? new Date(channel.createdAt).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default SlackChannels;
